/**
 * Telegram WebApp bootstrap + React hooks.
 *
 * The `init()` function is called once from `main.tsx` and wires:
 *   - WebApp.ready() / expand()
 *   - enableClosingConfirmation() (prevents accidental swipe-to-close)
 *   - theme param -> CSS variable sync
 *
 * The hooks (`useThemeParams`, `useBackButton`, `useMainButton`) subscribe
 * to the relevant WebApp events and provide an ergonomic React API.
 */

import { useEffect, useState, useCallback } from 'react';

/** Safe accessor - returns undefined in non-Telegram environments. */
export function webApp(): TelegramWebApp | undefined {
  return window.Telegram?.WebApp;
}

/**
 * One-time initialisation. Idempotent - calling twice is a no-op on the
 * second call because the underlying WebApp methods already handle
 * duplicate `ready()` calls.
 */
export function initTelegramWebApp(): void {
  const tg = webApp();
  if (!tg) return;

  try {
    tg.ready();
    tg.expand();
    tg.enableClosingConfirmation();

    // Sync initial theme into CSS variables.
    applyThemeParams(tg.themeParams, tg.colorScheme);

    // Subscribe to live theme changes.
    tg.onEvent('themeChanged', () => {
      applyThemeParams(tg.themeParams, tg.colorScheme);
    });
  } catch (err) {
    console.warn('[telegram] init failed', err);
  }
}

/**
 * Convert a Telegram hex color (e.g. `#0F172A`) to a Tailwind-friendly
 * RGB triplet like `15 23 42`.
 */
function hexToRgbTriplet(hex: string | undefined): string | undefined {
  if (!hex) return undefined;
  const m = /^#?([a-fA-F0-9]{6})$/.exec(hex.trim());
  if (!m) return undefined;
  const int = Number.parseInt(m[1]!, 16);
  const r = (int >> 16) & 0xff;
  const g = (int >> 8) & 0xff;
  const b = int & 0xff;
  return `${r} ${g} ${b}`;
}

function applyThemeParams(
  params: TelegramWebAppThemeParams,
  scheme: 'light' | 'dark',
): void {
  const root = document.documentElement;
  root.dataset.theme = scheme;

  const map: Record<string, string | undefined> = {
    '--bg-primary': hexToRgbTriplet(params.bg_color),
    '--bg-secondary': hexToRgbTriplet(params.secondary_bg_color),
    '--text-primary': hexToRgbTriplet(params.text_color),
    '--text-secondary': hexToRgbTriplet(params.hint_color),
    '--accent-purple': hexToRgbTriplet(params.button_color),
  };

  for (const [key, value] of Object.entries(map)) {
    if (value) root.style.setProperty(key, value);
  }
}

/** Hook: theme params + color scheme, kept in sync with Telegram events. */
export function useThemeParams(): {
  params: TelegramWebAppThemeParams;
  scheme: 'light' | 'dark';
} {
  const tg = webApp();
  const [state, setState] = useState<{
    params: TelegramWebAppThemeParams;
    scheme: 'light' | 'dark';
  }>(() => ({
    params: tg?.themeParams ?? {},
    scheme: tg?.colorScheme ?? 'dark',
  }));

  useEffect(() => {
    if (!tg) return;
    const handler = (): void => {
      setState({ params: tg.themeParams, scheme: tg.colorScheme });
    };
    tg.onEvent('themeChanged', handler);
    return () => tg.offEvent('themeChanged', handler);
  }, [tg]);

  return state;
}

/**
 * Hook: show/hide the Telegram BackButton and wire its click handler for
 * the lifetime of the component.
 */
export function useBackButton(onBack: (() => void) | null): void {
  const stable = useCallback(() => {
    onBack?.();
  }, [onBack]);

  useEffect(() => {
    const tg = webApp();
    if (!tg) return;
    if (!onBack) {
      tg.BackButton.hide();
      return;
    }
    tg.BackButton.show();
    tg.BackButton.onClick(stable);
    return () => {
      tg.BackButton.offClick(stable);
      tg.BackButton.hide();
    };
  }, [onBack, stable]);
}

export interface MainButtonConfig {
  text: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
  visible?: boolean;
}

/** Hook: drive the Telegram MainButton from React state. */
export function useMainButton(config: MainButtonConfig | null): void {
  useEffect(() => {
    const tg = webApp();
    if (!tg) return;

    if (!config || config.visible === false) {
      tg.MainButton.hide();
      return;
    }

    tg.MainButton.setText(config.text);
    tg.MainButton.show();

    if (config.loading) {
      tg.MainButton.showProgress();
    } else {
      tg.MainButton.hideProgress();
    }

    if (config.disabled) {
      tg.MainButton.disable();
    } else {
      tg.MainButton.enable();
    }

    const handler = (): void => config.onClick();
    tg.MainButton.onClick(handler);

    return () => {
      tg.MainButton.offClick(handler);
      tg.MainButton.hideProgress();
    };
  }, [config]);
}
